import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { RichNavigationLink } from "tsi-common-react/src/components/RichNavigationLink";
import { RichNavigationLinkCollectionContent } from "tsi-common-react/src/components/RichNavigationLinkCollection/index";

import { Context } from "@reactivated";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import iconCaretDown from "../../assets/svg/caret-down.svg";
import { CloseButton } from "../CloseButton";

export const DesktopRichNav = (
    props: Pick<
        NonNullable<
            React.ContextType<
                typeof Context
            >["chrome"]["site_header"]["desktop_rich_navigation"]
        >,
        "content"
    >,
) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const navRef = useRef<HTMLDivElement | null>(null);
    const triggerRefs = useRef<Record<number, HTMLButtonElement | null>>({});
    const blocks = props.content?.value ?? [];

    const toggle = (i: number) => {
        setOpenIndex((prev) => (prev === i ? null : i));
    };

    useEffect(() => {
        if (openIndex === null) {
            return;
        }
        const doc = getDocument();
        if (!doc) {
            return;
        }
        const activeIndex = openIndex;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") {
                return;
            }
            // Only pull focus back if it currently lives inside the nav —
            // otherwise Escape elsewhere on the page would yank it here.
            if (navRef.current?.contains(doc.activeElement)) {
                triggerRefs.current[activeIndex]?.focus();
            }
            setOpenIndex(null);
        };
        const onMouseDown = (e: MouseEvent) => {
            if (
                navRef.current &&
                e.target instanceof Node &&
                !navRef.current.contains(e.target)
            ) {
                setOpenIndex(null);
            }
        };
        doc.addEventListener("keydown", onKeyDown);
        doc.addEventListener("mousedown", onMouseDown);
        return () => {
            doc.removeEventListener("keydown", onKeyDown);
            doc.removeEventListener("mousedown", onMouseDown);
        };
    }, [openIndex]);

    return (
        <div className="tp-desktop-nav" ref={navRef}>
            <ul className="tp-desktop-nav__list">
                {blocks.map((block, i) => {
                    switch (block.type) {
                        case "rich_nav_link_collection": {
                            const isOpen = openIndex === i;
                            const panelId = `tp-desktop-nav-panel-${i}`;
                            return (
                                <li key={i} className="tp-desktop-nav__item">
                                    <button
                                        type="button"
                                        ref={(el) => {
                                            if (el) {
                                                triggerRefs.current[i] = el;
                                            } else {
                                                delete triggerRefs.current[i];
                                            }
                                        }}
                                        className="tp-desktop-nav__trigger"
                                        aria-expanded={isOpen}
                                        aria-controls={
                                            isOpen ? panelId : undefined
                                        }
                                        onClick={() => toggle(i)}
                                    >
                                        {block.value.name}
                                        <SVG
                                            src={iconCaretDown}
                                            aria-hidden="true"
                                        />
                                    </button>
                                    {isOpen && (
                                        <div
                                            id={panelId}
                                            className="tp-desktop-nav__panel"
                                        >
                                            <CloseButton
                                                className="tp-desktop-nav__close"
                                                iconClassName="tp-desktop-nav__close-icon"
                                                onClose={() => {
                                                    triggerRefs.current[
                                                        i
                                                    ]?.focus();
                                                    setOpenIndex(null);
                                                }}
                                            />
                                            <RichNavigationLinkCollectionContent
                                                value={block.value}
                                                isCurrentCollectionOpen={true}
                                                showPromosAtAllWidths
                                            />
                                        </div>
                                    )}
                                </li>
                            );
                        }
                        case "link":
                            return (
                                <li key={i} className="tp-desktop-nav__item">
                                    <RichNavigationLink {...block.value} />
                                </li>
                            );
                        default:
                            // `rich_nav_collection_container` is part of the
                            // shared RichNavigationContentAssembly palette but
                            // is not supported by the desktop trigger row.
                            // Editors can still select it on
                            // RichNavigationSettings.active_desktop_navigation,
                            // where it renders nothing. The durable fix is a
                            // desktop-specific StreamBlock that omits the
                            // container so it never reaches this branch.
                            return null;
                    }
                })}
            </ul>
        </div>
    );
};
